/**
 * <s-gallery-nav> — prev/next buttons for a horizontally scrolling gallery.
 *
 * Required markup:
 *   [data-gallery-track] — the scroll container.
 *   button[data-gallery-prev], button[data-gallery-next] — navigation buttons.
 */
class SGalleryNav extends HTMLElement {
  #ac;
  #track;
  #prev;
  #next;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-gallery-track]');
    this.#prev = this.querySelector('[data-gallery-prev]');
    this.#next = this.querySelector('[data-gallery-next]');
    if (!this.#track || !this.#prev || !this.#next) return;

    this.#prev.addEventListener('click', () => this.#go(-1), opts);
    this.#next.addEventListener('click', () => this.#go(1), opts);
    this.#track.addEventListener('scroll', () => this.#update(), { ...opts, passive: true });
    window.addEventListener('resize', () => this.#update(), opts);
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  get #dir() {
    return getComputedStyle(this.#track).direction === 'rtl' ? -1 : 1;
  }

  #go(step) {
    const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    this.#track.scrollBy({ left: step * this.#dir * this.#track.clientWidth, behavior });
  }

  #update() {
    const track = this.#track;
    const pos = Math.abs(track.scrollLeft);
    const max = track.scrollWidth - track.clientWidth;
    this.#prev.disabled = pos <= 1;
    this.#next.disabled = pos >= max - 1;
  }
}

if (!customElements.get('s-gallery-nav')) {
  customElements.define('s-gallery-nav', SGalleryNav);
}
